import { Box } from "@mui/material";
import React from "react";
import { ColorSystemProps, GOI_WEB_URL, getParlorPhoneByRouteParam } from "../../../constants";
import { icon } from "../../../constants/icon";
import { EventDomainType, EventPathType, EventTriggerAndTargetType } from "../../../types";
import { customEvent } from "../../../utils";
import { ColorIcon } from "../../common";
import { useParlorInquiryModal } from "../CrematoriumInquiry";

const GOI_CALL_NUMBER = "1666-9784";

// parlor.goifuneral.co.kr 데이터에 없어 /directions/{id}/ 가 404 인 장례식장 (2026-10-07 확인)
// ponytail: 하드코딩, parlor 앱 id 목록이 바뀌면 같이 갱신
const NO_DIRECTIONS_IDS = new Set([7, 908]);

interface Props {
  companyName: string;
  parlorId: number | string;
  eventDomain: keyof typeof EventDomainType;
  eventPath: EventPathType;
  mainColor: ColorSystemProps;
}

/** 장례식장 상세 상황별 바로가기. www ParlorIntentShortcuts 와 목적은 같고 구성, 목적지, 배치는 다르다 */
export function ParlorIntentLinks({ companyName, parlorId, eventDomain, eventPath, mainColor }: Props) {
  const { openInquiryModal } = useParlorInquiryModal({ eventDomain, eventPath });
  const utm = `utm_source=dogsound&utm_content=${eventDomain}`;
  const track = (trigger: EventTriggerAndTargetType) =>
    customEvent({ domain: eventDomain, path: eventPath, trigger_and_target: trigger, properties: { parlor_id: parlorId } });

  const tiles: { trigger: EventTriggerAndTargetType; href: string; iconName: keyof typeof icon; title: string; desc: string }[] = [
    {
      trigger: "it-mbs",
      href: `${GOI_WEB_URL}/match/request/?template=mubinso&${utm}&utm_medium=intent_mubinso`,
      iconName: "parlor",
      title: "무빈소 장례 알아보기",
      desc: "빈소 없이 치르는 장례 비용과 절차",
    },
  ];
  if (!NO_DIRECTIONS_IDS.has(Number(parlorId))) {
    tiles.push({
      trigger: "it-visit",
      href: `https://parlor.goifuneral.co.kr/directions/${parlorId}/?${utm}&utm_medium=intent_visit`,
      iconName: "location",
      title: "조문을 가려고 해요",
      desc: "위치, 주차, 대중교통 안내",
    });
  }

  return (
    <Box component="section" style={{ padding: "8px 20px 32px" }}>
      <Box style={{ maxWidth: "720px", margin: "0 auto" }}>
        <Box component="h2" style={{ fontSize: "18px", fontWeight: 700, color: "#252525", margin: 0, lineHeight: 1.4 }}>
          {companyName} 바로 안내
        </Box>
        <Box component="p" style={{ margin: "4px 0 14px", fontSize: "13px", color: "#888" }}>
          e하늘 장사정보시스템 공개 정보를 바탕으로 고이장례연구소가 안내해요.
        </Box>

        <Box
          component="a"
          href={`tel:${GOI_CALL_NUMBER}`}
          onClick={(e) => {
            e.preventDefault();
            track("it-call");
            // 하단 고정바와 같은 문의 유형 모달. 빈소, 이송 문의만 새벽에도 연결된다
            openInquiryModal(getParlorPhoneByRouteParam(String(parlorId)));
          }}
          style={{ display: "flex", alignItems: "center", gap: "14px", padding: "16px", borderRadius: "14px", background: mainColor.c400, color: "#fff", textDecoration: "none" }}
        >
          <Box style={{ width: "40px", height: "40px", borderRadius: "12px", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ColorIcon name="icon_call_mono" color="#fff" size={22} />
          </Box>
          <Box style={{ flex: 1 }}>
            <Box style={{ fontSize: "16px", fontWeight: 700 }}>빠른 장례상담, 임종접수가 필요해요</Box>
            <Box style={{ marginTop: "2px", fontSize: "13px", opacity: 0.9 }}>24시간 바로 전화 연결, 고이에서 모두 도와드려요</Box>
          </Box>
          <ColorIcon name="arrow_right_small" color="#fff" size={20} />
        </Box>

        <Box style={{ display: "grid", gridTemplateColumns: `repeat(${tiles.length}, 1fr)`, gap: "10px", marginTop: "10px" }}>
          {tiles.map((t) => (
            <Box
              key={t.trigger}
              component="a"
              href={t.href}
              onClick={() => track(t.trigger)}
              style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "16px", borderRadius: "14px", border: `1px solid ${mainColor.c200}`, background: "#fff", textDecoration: "none" }}
            >
              <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box style={{ width: "36px", height: "36px", borderRadius: "10px", background: mainColor.c100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ColorIcon name={t.iconName} color={mainColor.c500} size={20} />
                </Box>
                <ColorIcon name="arrow_right_small" color="#aaa" size={18} />
              </Box>
              <Box>
                <Box style={{ fontSize: "15px", fontWeight: 700, color: "#252525" }}>{t.title}</Box>
                <Box style={{ marginTop: "2px", fontSize: "12px", color: "#686868", lineHeight: 1.5 }}>{t.desc}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
